import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import OtpInput from "react-otp-input";
import PartnerFooter from "../../components/PartnerFooter";
import ScreenHeader from "../../components/ScreenHeader";
import "./otpValidation.scss";

// V2 OTP page (route "/otp-validation", gated inside screens/otpValidation.jsx).
// The V1 screen keeps owning VerifySmsCode, the post-verify dispatches and the
// navigation — they arrive here as onSubmit; resends (SMS or voice call) as
// onResend. The 60s resend cooldown is pure presentation, so it lives here.
// No back control: like the phone step, this is a forced auth gate.
function OtpValidation({ displayError, onSubmit, onResend }) {
  const { t } = useTranslation();
  const [otp, setOtp] = useState("");
  const [timeLeft, setTimeLeft] = useState(0);
  const [displayNoCodeReceived, setDisplayNoCodeReceived] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (timeLeft > 0 && !intervalRef.current) {
      intervalRef.current = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    };
  }, [timeLeft > 0]);

  const handleResend = (isPhoneCall) => {
    if (timeLeft > 0) return;
    setDisplayNoCodeReceived(true);
    setTimeLeft(60);
    onResend(isPhoneCall);
  };

  return (
    <div className="otp-v2">
      <ScreenHeader title={t("PHONE_VALIDATION.TITLE")} variant="none" />

      <div className="otp-v2-content">
        <p className="otp-v2-subtitle">{t("OTP_VALIDATION.ENTER_CODE")}</p>

        <div className="otp-v2-boxes">
          <OtpInput
            value={otp}
            onChange={setOtp}
            numInputs={4}
            renderSeparator={<span className="otp-v2-separator" />}
            renderInput={(props) => <input {...props} className="otp-v2-box" />}
            inputType="number"
            shouldAutoFocus={false}
          />
        </div>

        <button type="button" className="otp-v2-cta" onClick={() => onSubmit(otp)} disabled={otp.length !== 4}>
          {t("PHONE_VALIDATION.CONTINUE")}
        </button>

        <button type="button" className="otp-v2-link" onClick={() => handleResend(true)}>
          {t("OTP_VALIDATION.SEND_VOICE_CODE")}
        </button>

        {displayNoCodeReceived && (
          <div className="otp-v2-support">
            <p>{t("OTP_VALIDATION.DIDNT_GET_SMS")}</p>
            <p>
              {t("OTP_VALIDATION.CONTACT_SUPPORT")} <a href="tel:033730300">03-3730300</a>
            </p>
          </div>
        )}

        {displayError && <p className="otp-v2-error">{t("OTP_VALIDATION.WRONG_CODE")}</p>}

        <div className="otp-v2-resend">
          <span className="otp-v2-resend-question">{t("OTP_VALIDATION.DIDNT_GET_CODE")}</span>
          <button type="button" className="otp-v2-link" onClick={() => handleResend(false)}>
            {t("OTP_VALIDATION.SEND_AGAIN")}
          </button>
          {timeLeft > 0 && (
            <span className="otp-v2-countdown">{t("OTP_VALIDATION.SEND_AGAIN_IN", { seconds: timeLeft })}</span>
          )}
        </div>
      </div>

      <PartnerFooter />
    </div>
  );
}

export default OtpValidation;
